import {useState, useMemo, useCallback, useEffect, useRef} from 'react';
import {useDebounce} from './useDebounce';

/**
 * Recursively collects all text ranges from a ProseMirror node tree.
 */
function collectBlockText(node, blockIndex, acc) {
    if (!node) return;
    if (typeof node.text === 'string') {
        acc.push({blockIndex, text: node.text, start: 0, end: 0});
        return;
    }
    if (Array.isArray(node.content)) {
        node.content.forEach((child) => collectBlockText(child, blockIndex, acc));
    }
}

/**
 * Returns the concatenated text of all descendant text nodes in `node`.
 */
function nodeText(node) {
    if (!node) return '';
    if (typeof node.text === 'string') return node.text;
    if (!Array.isArray(node.content)) return '';
    return node.content.map(nodeText).join('');
}

/**
 * Finds all occurrences of `query` (case-insensitive) inside `text`,
 * returning an array of { start, end } char-offset pairs.
 */
function findOccurrences(text, query) {
    const results = [];
    if (!query || !text) return results;
    const lower = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    let pos = 0;
    while (pos < lower.length) {
        const idx = lower.indexOf(lowerQuery, pos);
        if (idx < 0) break;
        results.push({start: idx, end: idx + query.length});
        pos = idx + 1;
    }
    return results;
}

/**
 * Builds a flat array of per-block text strings from a ProseMirror doc.
 * Result is stable as long as `pmContent` identity is unchanged — callers can
 * memoize it separately so repeated queries over the same document skip the
 * tree-walk entirely.
 */
function buildBlockTexts(pmContent) {
    if (!pmContent) return [];
    const topBlocks = Array.isArray(pmContent?.content) ? pmContent.content : [];
    return topBlocks.map(nodeText);
}

/**
 * Builds the full match list from pre-computed block texts and a search query.
 * Each match: { blockIndex: number, start: number, end: number, text: string }
 */
function buildMatches(pmContent, query, blockTexts) {
    if (!pmContent || !query || query.trim().length < 2) return [];
    const texts = blockTexts || buildBlockTexts(pmContent);
    const q = query.trim();
    const matches = [];
    texts.forEach((fullText, blockIndex) => {
        const occurrences = findOccurrences(fullText, q);
        occurrences.forEach(({start, end}) => {
            matches.push({blockIndex, start, end, text: fullText.slice(start, end)});
        });
    });
    return matches;
}

/**
 * useDocumentSearch — in-document Ctrl+F style search over a ProseMirror doc.
 *
 * @param {object|null} pmContent - The ProseMirror doc node ({ type: 'doc', content: [...] })
 * @returns {{
 *   query: string,
 *   setQuery: (q: string) => void,
 *   matches: Array<{blockIndex: number, start: number, end: number, text: string}>,
 *   activeIdx: number,
 *   navigate: (dir: 'next'|'prev') => void,
 *   clear: () => void,
 * }}
 */
export function useDocumentSearch(pmContent) {
    const [query, setQueryRaw] = useState('');
    const [activeIdx, setActiveIdx] = useState(-1);

    const debouncedQuery = useDebounce(query, 50);

    // Memoize the per-block text array separately from the query so that typing
    // does NOT re-walk the entire PM tree — only a new document triggers a re-walk.
    const blockTexts = useMemo(() => buildBlockTexts(pmContent), [pmContent]);

    const matches = useMemo(
        () => buildMatches(pmContent, debouncedQuery, blockTexts),
        // blockTexts already tracks pmContent identity; no need to list pmContent.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [blockTexts, debouncedQuery],
    );

    // Keep a ref so navigate's setState updater never captures a stale count.
    const matchesCountRef = useRef(matches.length);
    matchesCountRef.current = matches.length;

    // When the match list identity changes (new query result or new doc), reset to match 0.
    // Must be useEffect — never call setState inside useMemo.
    const prevMatchesRef = useRef(null);
    useEffect(() => {
        if (prevMatchesRef.current === matches) return;
        prevMatchesRef.current = matches;
        setActiveIdx(matches.length > 0 ? 0 : -1);
    }, [matches]);

    const setQuery = useCallback((q) => {
        setQueryRaw(q);
        // activeIdx resets via the useEffect above once the debounced query fires and matches update.
    }, []);

    const navigate = useCallback((dir) => {
        setActiveIdx((prev) => {
            const count = matchesCountRef.current;
            if (count === 0) return -1;
            if (prev < 0) return 0;
            if (dir === 'next') return (prev + 1) % count;
            return (prev - 1 + count) % count;
        });
    }, []);

    const clear = useCallback(() => {
        setQueryRaw('');
        setActiveIdx(-1);
    }, []);

    return {query, setQuery, matches, activeIdx, navigate, clear};
}

export {buildMatches, buildBlockTexts, findOccurrences, nodeText};
